import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { initNativeShell } from "@/lib/native";

initNativeShell();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register the service worker on app boot for PWA installability + auto-update.
// Flow:
//   1. Browser fetches /sw.js. If its byte-content differs from the cached
//      copy (we stamp __BUILD_VERSION__ into it at build time), a new SW
//      enters the "waiting" state.
//   2. We immediately tell it to skipWaiting so it activates.
//   3. When the new SW takes control, `controllerchange` fires → reload once.
//   4. While the app is open, we poll reg.update() every 60s and on
//      focus/visibilitychange so long-lived sessions catch deploys.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        // Reload the page once the new SW takes control.
        let reloaded = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (reloaded) return;
          reloaded = true;
          window.location.reload();
        });

        const promoteWaiting = (worker) => {
          if (worker && worker.state === 'installed' && navigator.serviceWorker.controller) {
            worker.postMessage({ type: 'SKIP_WAITING' });
          }
        };

        // If there's already a waiting worker on boot, promote it.
        if (reg.waiting) promoteWaiting(reg.waiting);

        // When a new SW is found, listen for its state transitions.
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener('statechange', () => promoteWaiting(nw));
        });

        // Periodic update checks. The browser will only fetch sw.js again
        // if HTTP cache allows it; reg.update() bypasses normal caches.
        const checkForUpdate = () => { reg.update().catch(() => {}); };
        setInterval(checkForUpdate, 60_000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdate();
        });
        window.addEventListener('focus', checkForUpdate);
      })
      .catch((err) => console.warn('SW register failed', err));
  });
}
